import Service from '@ember/service';
import ENV from '../config/environment';
import { set } from '@ember/object';
import { service } from '@ember/service';
import { saveDesignAnalytics, loadDesignAnalytics, loadDesignFailureAnalytics } from '../utils/analytics';
import { getResponseType, getResponseValue } from '../models/gestures/filter-response-gesture';
import IndexModel from '../models/index';
import SpecificationAdvisor from '../models/advisors/specification';
import ComponentsAdvisor from '../models/advisors/component-selection';
import TolerancesAdvisor from '../models/advisors/tolerance';
import FilterConstants from '../filter-constants';
import { parser, processResults, generateJsonString } from './persistence/tokenize';
import {
  dryVisitedTabs,
  dryFilter,
  drySpecifications,
  dryComponents,
  dryTolerances,
  buildCircuitComponents,
} from './persistence/dryer';
import * as CommonUtils from '../utils/common';
import { dasherize, camelize } from '@ember/string';

let validNumber = function (value) {
  return typeof value !== 'undefined' && value !== null && !isNaN(value);
};

let getInvalidOpAmps = function (stages, opamps) {
  // Build list of OpAmps from Stages
  let allOpAmps = [];
  stages.forEach((stage) => {
    if (!allOpAmps.includes(stage.opAmp)) {
      allOpAmps.push(stage.opAmp);
    }
  });

  // Return OpAmps that are not  Existing in the list
  return allOpAmps.filter((opAmp) => {
    let result = opamps.getOpAmp(opAmp);
    return opAmp !== 'ideal' && (typeof result === 'undefined' || result === null);
  });
};

let getOrdinal = function (n) {
  let s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

let compareDesignVersion = function (ver1, ver2) {
  let v1 = ver1.split('.');
  let v2 = ver2.split('.');
  const k = Math.min(v1.length, v2.length);
  for (let i = 0; i < k; ++i) {
    v1[i] = parseInt(v1[i], 10);
    v2[i] = parseInt(v2[i], 10);
    if (v1[i] > v2[i]) return 1;
    if (v1[i] < v2[i]) return -1;
  }
  return v1.length === v2.length ? 0 : v1.length < v2.length ? -1 : 1;
};

// Persistence Service: Service to handle application save and load.

export default class PersistenceService extends Service {
  @service('aditools-analytics') analytics;
  @service logger;
  @service opamps;
  version = null;

  _getStageImplementation(designData, index) {
    const name = 'stage' + (index + 1);
    return name in designData.stageImplementations ? designData.stageImplementations[name] : '';
  }
  _cleanLoadedImplementation(loadedImplementation, individualStage) {
    return individualStage.implementationSelection.selections.includes(loadedImplementation)
      ? loadedImplementation
      : individualStage.implementation;
  }

  getSaveData(visitedTabs, inputModel, componentsAdvisor) {
    let design = { visitedTabs: '', filterType: '', specifications: {}, components: {}, tolerances: {}, nextSteps: {} };
    let tabList = visitedTabs ? visitedTabs : ['filter-type'];
    let indexModel = new IndexModel(this.opamps);

    design.visitedTabs = tabList;

    tabList.forEach((tab) => {
      switch (tab) {
        case 'filter-type':
          indexModel.inputs.filterType = inputModel.filterType;
          design.filterType = inputModel.filterType ? inputModel.filterType : {};
          break;
        case 'specifications':
          indexModel.inputs.specifications = inputModel.specifications;
          design.specifications = this.createSpecificationsDesignData(inputModel.specifications);
          break;
        case 'components':
          indexModel.inputs.components = componentsAdvisor.restructure(inputModel.components);
          design.components = this.createComponentsDesignData(
            indexModel.inputs.components,
            indexModel.results.components.detailsOfStages,
            indexModel.results.components.needReferenceStage,
          );
          break;
        case 'tolerances':
          indexModel.inputs.tolerances = inputModel.tolerances;
          design.tolerances = this.createTolerancesDesignData(inputModel.tolerances);
          break;
        case 'next-steps':
          if (inputModel.nextSteps) indexModel.inputs.nextSteps = inputModel.nextSteps;
          design.nextSteps = inputModel.nextSteps ? inputModel.nextSteps : {};
          break;
      }
    });
    return { design: design, model: indexModel };
  }
  createSpecificationsDesignData(data) {
    let designData = {};
    designData.gain = data.gain;
    designData.gainUnit = data.gainUnit;
    designData.passbandAttenuation = data.passbandAttenuation;
    designData.passbandFrequency = data.passbandFrequency;
    designData.stopbandAttenuation = data.stopbandAttenuation;
    designData.stopbandFrequency = data.stopbandFrequency;
    designData.filterResponseValue = data.filterResponseValue ? data.filterResponseValue : 0;
    designData.filterResponseSlider = data.filterResponseSlider;
    if (typeof data.centerFrequency !== 'undefined') {
      designData.centerFrequency = data.centerFrequency;
    }
    return designData;
  }
  createComponentsDesignData(data, stageInfo, needReferenceStage) {
    let designData = {};
    designData.vsPlus = data.vsPlus;
    designData.vsMinus = data.vsMinus;
    designData.optimization = data.optimization;
    designData.optimizationPreference = data.optimizationPreference;
    designData.compensateForGbw = data.compensateForGbw;
    designData.preferredAlgorithms = data.preferredAlgorithms;
    designData.referenceVoltage = needReferenceStage ? (data.vsPlus + data.vsMinus) / 2 : 0;

    // set component sizing slider, gain slider, and opAmp values
    let stages = [];
    for (const stage of data.individualStageInputs) {
      stages.push({
        stageLetter: stage.stageLetter,
        componentSizing: stage.componentSizing.value,
        gain: stage.gainRatio.value,
        gainEnabled: stage.gainRatio.enabled,
        implementation: stage.implementation,
        opAmps: stage.opAmp,
      });
    }
    designData.stages = stages;
    // if (typeof(data.recommendedAmps) !== 'undefined' && data.recommendedAmps.length > 0) {
    //   designData.recommendedAmps = data.recommendedAmps;
    // }
    designData.recommendedAmps = [];

    // Sort Order for Pick for me must also be taken into consideration
    if (designData.optimizationPreference === FilterConstants.optimizationPreferenceOptions.nonSpecific) {
      let sortOrder = stageInfo.map((item) => {
        return item.actualStage.stageId - 1;
      });
      designData.sortOrder = sortOrder;
    } else {
      designData.sortOrder = data.sortOrder;
    }
    return designData;
  }
  createTolerancesDesignData(data) {
    let designData = {};
    designData.resistorTolerance = data.resistorTolerance;
    designData.capacitorTolerance = data.capacitorTolerance;
    designData.inductorTolerance = data.inductorTolerance;
    designData.opAmpGbwTolerance = data.opAmpGbwTolerance;
    designData.resistorPreferredSeries = data.resistorPreferredSeries;
    designData.capacitorPreferredSeries = data.capacitorPreferredSeries;
    designData.inductorPreferredSeries = data.inductorPreferredSeries;
    return designData;
  }
  getLoadData(visitedTabs, designData) {
    const indexModel = new IndexModel(this.opamps);
    let tabList = visitedTabs ? visitedTabs : ['filter-type'];

    if (this.version === 'old') {
      tabList = this.buildVisitedTabs(designData);
    }

    tabList.forEach((tab) => {
      switch (tab) {
        case 'filter-type':
          indexModel.inputs.filterType = designData.filterType
            ? designData.filterType
            : designData.type
              ? designData.type.filterType
              : null;
          break;
        case 'specifications':
          indexModel.inputs.specifications = this.populateSpecificationsModel(indexModel, designData.specifications);
          break;
        case 'components':
          indexModel.inputs.components = this.populateComponentsModel(
            indexModel,
            designData.components ? designData.components : designData.compSelect,
          );
          break;
        case 'tolerances':
          indexModel.inputs.tolerances = this.populateTolerancesModel(
            indexModel,
            designData.tolerances ? designData.tolerances : designData.compTolerance,
          );
          break;
        case 'next-steps':
          indexModel.inputs.nextSteps = null;
          break;
      }
    });
    const inputModel = indexModel.inputs;
    return { visitedTabs: tabList, inputModel: inputModel };
  }
  buildVisitedTabs(designData) {
    let tablist = [];
    for (let item of ['type', 'specifications', 'compSelect', 'compTolerance', 'finalResults']) {
      if (item in designData) {
        switch (item) {
          case 'type':
            if (designData.type && designData.type.filterType) {
              tablist.push('filter-type');
            }
            break;
          case 'specifications':
            if (tablist.includes('filter-type')) {
              tablist.push('specifications');
            }
            break;
          case 'compSelect':
            if (tablist.includes('specifications')) {
              tablist.push('components');
            }
            break;
          case 'compTolerance':
            if (tablist.includes('components')) {
              tablist.push('tolerances');
            }
            break;
          case 'finalResults':
            if (tablist.includes('tolerances')) {
              tablist.push('next-steps');
            }
            break;
        }
      }
    }
    return tablist;
  }
  populateSpecificationsModel(indexModel, designData) {
    let advisor = new SpecificationAdvisor(indexModel);
    let inputData = advisor.generate();
    set(inputData, 'gain', validNumber(designData.gain) ? parseFloat(designData.gain) : inputData.gain);
    let gainUnit = designData.gainUnit ? designData.gainUnit : designData.gainUnitsSetting; // old design uses gainUnitsSetting, newer designs use gainUnit
    gainUnit = gainUnit === FilterConstants.gainUnits.db || gainUnit === FilterConstants.gainUnits.vv ? gainUnit : null;
    set(inputData, 'gainUnit', gainUnit ? gainUnit : inputData.gainUnit);
    set(
      inputData,
      'passbandAttenuation',
      validNumber(designData.passbandAttenuation)
        ? parseFloat(designData.passbandAttenuation)
        : inputData.passbandAttenuation,
    );
    set(
      inputData,
      'passbandFrequency',
      validNumber(designData.passbandFrequency)
        ? parseFloat(designData.passbandFrequency)
        : inputData.passbandFrequency,
    );
    set(
      inputData,
      'stopbandAttenuation',
      validNumber(designData.stopbandAttenuation)
        ? parseFloat(designData.stopbandAttenuation)
        : inputData.stopbandAttenuation,
    );
    set(
      inputData,
      'stopbandFrequency',
      validNumber(designData.stopbandFrequency)
        ? parseFloat(designData.stopbandFrequency)
        : inputData.stopbandFrequency,
    );
    if (indexModel.inputs.filterType === FilterConstants.filterType.bandPass) {
      set(
        inputData,
        'centerFrequency',
        validNumber(designData.centerFrequency) ? parseFloat(designData.centerFrequency) : inputData.centerFrequency,
      );
    }
    let filterResponseSlider =
      this.version === 'new' ? designData.filterResponseSlider : designData.filterResponseSliderValue; // old design uses filterResponseSliderValue, newer designs use filterResponseSlider
    set(
      inputData,
      'filterResponseSlider',
      validNumber(filterResponseSlider) ? parseFloat(filterResponseSlider) : inputData.filterResponseSlider,
    );
    set(inputData, 'selectedResponseType', getResponseType(inputData.filterResponseSlider));
    set(
      inputData,
      'filterResponseValue',
      getResponseValue(inputData.filterResponseSlider, inputData.passbandAttenuation),
    );
    return inputData;
  }
  populateComponentsModel(indexModel, designData) {
    let advisor = new ComponentsAdvisor(indexModel);
    let inputData = advisor.generate();
    set(inputData, 'preferredAlgorithms', designData.preferredAlgorithms);
    set(inputData, 'optimization', designData.optimization);
    set(inputData, 'optimizationPreference', designData.optimizationPreference);
    set(inputData, 'recommendedAmps', designData.recommendedAmps ? designData.recommendedAmps : []); // new design data is set to [] for now
    let individualStages = inputData.individualStageInputs;
    let sortOrder = [];
    // New versions are versions with 1 or greater, it is considered old when it has no version and is set to 0
    // We may change the if statements into case statements when newer versions introduce newer properties
    // Here is a list of the differences of the design file property names for new and old in components
    //     1. Positive Supply Voltage - vsPlus (new); plusVs (old)
    //     2. Negative Supply Voltage - vsMinus (new); minusVs (old)
    //     3. Compensate for GBW - compensateForGbw (new); compensateGBW (old)
    //     4. Stage Letter - stages[index]stageLetter (new); stage (old)
    //     5. Implementation - stages[index].implementation (new); stageImplementations[index] (old)
    //     6. Op Amp Part Name - stages[index].opAmps (new); opAmps[index].value (old)
    // For slider values, the new one is saved at stages[index].componentSizing and stages[index].gain, while the old ones
    // use rcSizing[index].value and gain[index].value. However, the old designs may have a rcSizing that has a rawVal property.
    // Also these component and gain values for old designs are stored into legacySizingSliderValue and legacyGainSliderValue
    if (this.version === 'new') {
      set(inputData, 'vsPlus', validNumber(designData.vsPlus) ? parseFloat(designData.vsPlus) : inputData.vsPlus);
      set(inputData, 'vsMinus', validNumber(designData.vsMinus) ? parseFloat(designData.vsMinus) : inputData.vsMinus);
      set(inputData, 'compensateForGbw', designData.compensateForGbw);
      for (let i = 0; i < designData.stages.length; i++) {
        set(
          individualStages[i],
          'stageLetter',
          designData.stages[i].stageLetter ? designData.stages[i].stageLetter : individualStages[i].stageLetter,
        );
        set(
          individualStages[i],
          'implementation',
          designData.stages[i].implementation
            ? designData.stages[i].implementation
            : individualStages[i].implementation,
        );
        set(individualStages[i].implementationSelection, 'selectedItem', individualStages[i].implementation);
        set(
          individualStages[i],
          'opAmp',
          designData.stages[i].opAmps ? designData.stages[i].opAmps : individualStages[i].opAmp,
        );
        set(
          individualStages[i].componentSizing,
          'value',
          typeof designData.stages[i].componentSizing !== 'undefined' &&
            designData.stages[i].componentSizing !== null &&
            !isNaN(designData.stages[i].componentSizing)
            ? designData.stages[i].componentSizing
            : individualStages[i].componentSizing.value,
        );
        set(individualStages[i].componentSizing, 'minimum', -1); // This will be properly set when the components advisor updates the main model
        set(individualStages[i].componentSizing, 'maximum', -1); // This will be properly set when the components advisor updates the main model
        set(
          individualStages[i].gainRatio,
          'value',
          typeof designData.stages[i].gain !== 'undefined' &&
            designData.stages[i].gain !== null &&
            !isNaN(designData.stages[i].gain)
            ? designData.stages[i].gain
            : individualStages[i].gainRatio.value,
        );
        set(individualStages[i].gainRatio, 'minimum', -1); // This will be properly set when the components advisor updates the main model
        set(individualStages[i].gainRatio, 'maximum', -1); // This will be properly set when the components advisor updates the main model
        set(
          individualStages[i].gainRatio,
          'enabled',
          designData.stages[i].gainEnabled ? designData.stages[i].gainEnabled : individualStages[i].gainRatio.enabled,
        );
      }
      sortOrder = designData.sortOrder;
    } else if (this.version === 'old') {
      set(inputData, 'vsPlus', validNumber(designData.plusVs) ? parseFloat(designData.plusVs) : inputData.vsPlus);
      set(inputData, 'vsMinus', validNumber(designData.minusVs) ? parseFloat(designData.minusVs) : inputData.vsMinus);
      set(inputData, 'compensateForGbw', designData.compensateGBW);
      for (let i = 0; i < designData.opAmps.length; i++) {
        set(
          individualStages[i],
          'stageLetter',
          designData.opAmps[i].stage ? designData.opAmps[i].stage : individualStages[i].stageLetter,
        );
        set(
          individualStages[i],
          'implementation',
          this._cleanLoadedImplementation(this._getStageImplementation(designData, i), individualStages[i]),
        );
        set(individualStages[i].implementationSelection, 'selectedItem', individualStages[i].implementation);
        set(
          individualStages[i],
          'opAmp',
          designData.opAmps[i].value ? designData.opAmps[i].value : individualStages[i].opAmp,
        );
        set(individualStages[i].componentSizing, 'value', null);
        set(individualStages[i].componentSizing, 'minimum', -1); // This will be properly set when the components advisor updates the main model
        set(individualStages[i].componentSizing, 'maximum', -1); // This will be properly set when the components advisor updates the main model
        set(individualStages[i].gainRatio, 'value', null);
        set(individualStages[i].gainRatio, 'minimum', -1); // This will be properly set when the components advisor updates the main model
        set(individualStages[i].gainRatio, 'maximum', -1); // This will be properly set when the components advisor updates the main model
        set(individualStages[i].gainRatio, 'enabled', designData.gain[i] ? !designData.gain[i].disabled : null);
        set(
          individualStages[i],
          'legacySizingSliderValue',
          designData.rcSizing[i]
            ? designData.rcSizing[i].rawVal
              ? designData.rcSizing[i].rawVal
              : designData.rcSizing[i].value
            : null,
        );
        set(
          individualStages[i],
          'legacyGainSliderValue',
          designData.gain[i] ? (designData.gain[i].value ? designData.gain[i].value : null) : null,
        );
      }
      sortOrder = this.rebuildSortOrder(designData.sortOrder);
    }
    set(inputData, 'individualStageInputs', individualStages);
    set(inputData, 'sortOrder', sortOrder);
    return inputData;
  }
  populateTolerancesModel(indexModel, designData) {
    // New versions are versions with 1 or greater, it is considered old when it has no version and is set to 0
    // We may change the if statements into case statements when newer versions introduce newer properties
    // Here is a list of the differences of the design file property names for new and old in tolerances
    //     1. Resistor Tolerance - resistorTolerance (new); resistor (old)
    //     2. Capacitor Tolerance - capacitorTolerance (new); capacitor (old)
    //     3. Inductor Tolerance - inductorTolerance (new); inductor (old)
    //     4. Op Amp GBW Tolerance - opAmpGbwTolerance (new); gbw (old)
    //     5. Resistor Preferred Series - resistorPreferredSeries (new); prefResistors (old)
    //     6. Capacitor Preferred Series - capacitorPreferredSeries (new); prefCapacitors (old)
    //     7. Inductor Preferred Series - inductorPreferredSeries (new); prefInductors (old)
    let advisor = new TolerancesAdvisor(indexModel);
    let inputData = advisor.generate();
    let resistorTolerance = designData.resistorTolerance ? designData.resistorTolerance : designData.resistor;
    set(inputData, 'resistorTolerance', resistorTolerance ? resistorTolerance : inputData.resistorTolerance);
    let capacitorTolerance = designData.capacitorTolerance ? designData.capacitorTolerance : designData.capacitor;
    set(inputData, 'capacitorTolerance', capacitorTolerance ? capacitorTolerance : inputData.capacitorTolerance);
    let inductorTolerance = designData.inductorTolerance ? designData.inductorTolerance : designData.inductor;
    set(inputData, 'inductorTolerance', inductorTolerance ? inductorTolerance : inputData.inductorTolerance);
    let opAmpGbwTolerance = designData.opAmpGbwTolerance ? designData.opAmpGbwTolerance : designData.gbw;
    set(inputData, 'opAmpGbwTolerance', opAmpGbwTolerance ? opAmpGbwTolerance : inputData.opAmpGbwTolerance);
    let resistorPreferredSeries = designData.resistorPreferredSeries
      ? designData.resistorPreferredSeries
      : designData.prefResistors;
    set(
      inputData,
      'resistorPreferredSeries',
      resistorPreferredSeries ? resistorPreferredSeries : inputData.resistorPreferredSeries,
    );
    let capacitorPreferredSeries = designData.capacitorPreferredSeries
      ? designData.capacitorPreferredSeries
      : designData.prefCapacitors;
    set(
      inputData,
      'capacitorPreferredSeries',
      capacitorPreferredSeries ? capacitorPreferredSeries : inputData.capacitorPreferredSeries,
    );
    let inductorPreferredSeries = designData.inductorPreferredSeries
      ? designData.inductorPreferredSeries
      : designData.prefInductors;
    set(
      inputData,
      'inductorPreferredSeries',
      inductorPreferredSeries ? inductorPreferredSeries : inputData.inductorPreferredSeries,
    );
    return inputData;
  }
  rebuildSortOrder(order) {
    let sortOrder = [];
    for (let i = 0; i < order.length; i++) {
      sortOrder.push(order[i] - 1);
    }
    return sortOrder;
  }
  save(name, desc, tabs, inputs, componentsAdvisor) {
    let design = this.getSaveData(tabs, inputs, componentsAdvisor).design;
    let now = new Date();
    let ticks = now.getTime();
    let fileName = (name.length > 0 ? name : 'SavedDesign' + ENV.toolname.toUpperCase() + ticks.toString()) + '.json';
    let saveDateStamp =
      now.getFullYear() +
      '-' +
      ('0' + (now.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + now.getDate()).slice(-2) +
      ' ' +
      ('0' + (((now.getHours() + 11) % 12) + 1)).slice(-2) +
      ':' +
      ('0' + now.getMinutes()).slice(-2) +
      ':' +
      ('0' + now.getSeconds()).slice(-2) +
      ' ' +
      (now.getHours() >= 12 ? 'PM' : 'AM');
    let version = '1.3';
    let toolName = 'FW';

    let designData = {
      Name: name,
      Description: desc,
      Tool: toolName,
      Version: version,
      Design: design,
      CreatedDate: saveDateStamp,
      UpdatedDate: saveDateStamp,
    };
    let jsonStringData = JSON.stringify(designData, null, '\t');

    let blob = new Blob([jsonStringData], { type: 'application/octet-stream; charset=UTF-8', encoding: 'UTF-8' });

    if (typeof window.navigator.msSaveBlob !== 'undefined') {
      window.navigator.msSaveBlob(blob, fileName);
    } else {
      let URL = window.URL || window.webkitURL;
      let downloadUrl = URL.createObjectURL(blob);

      if (fileName) {
        let a = document.createElement('a');
        if (typeof a.download === 'undefined') {
          window.location = downloadUrl;
        } else {
          a.href = downloadUrl;
          a.download = fileName;
          a.click();
        }
      } else {
        window.location = downloadUrl;
      }
      URL.revokeObjectURL(downloadUrl);
    }
    saveDesignAnalytics(name, desc, saveDateStamp, this.analytics);
  }
  load(jsonStringData) {
    let returnData = null;
    let inputs = null;
    let parseData = null;
    let errorCode = { code: FilterConstants.loadErrorCodes.noError, details: '' };

    try {
      if (this.opamps.getOpAmps() !== null) {
        parseData = JSON.parse(jsonStringData);
        let isValid = true;
        let version = parseData.Version ? 'new' : 'old';
        set(this, 'version', version);

        if (parseData.Name) isValid = isValid && parseData.Name.length > 0;
        if (parseData.Description) isValid = isValid && parseData.Description.length > 0;
        if (parseData.Tool) isValid = isValid && parseData.Tool === 'FW';
        if (parseData.CreatedDate) isValid = isValid && parseData.CreatedDate.length > 0;
        if (parseData.UpdatedDate) isValid = isValid && parseData.UpdatedDate.length > 0;

        let designData =
          typeof parseData.Design !== 'undefined' && parseData.Design !== null ? parseData.Design : parseData; // this is for cases in old designs where the tab data isn't consolidated into "Design" object
        if (isValid) {
          if (version === 'new') {
            isValid = isValid && designData.filterType !== null;
            isValid = isValid && designData.specifications !== null;
            isValid = isValid && designData.components !== null;
            isValid = isValid && designData.tolerances !== null;
            isValid = isValid && designData.nextSteps !== null;
          } else {
            isValid = isValid && designData.type !== null;
            if (!designData.specifications) designData.specifications = {};
            isValid = isValid && designData.compSelect !== null;
            isValid = isValid && designData.compTolerance !== null;
            isValid = isValid && designData.finalResults !== null;
          }
        }
        if (isValid) {
          // Build design date only when valid
          inputs = this.getLoadData(designData.visitedTabs, designData);

          // Check list of parts if they are existing in the product list (provided that your design data contains components section)
          if (
            inputs.inputModel.components &&
            inputs.inputModel.components.individualStageInputs &&
            inputs.inputModel.components.individualStageInputs.length > 0
          ) {
            let invalidOpAmps = getInvalidOpAmps(inputs.inputModel.components.individualStageInputs, this.opamps);
            if (invalidOpAmps.length > 0) {
              errorCode.code = FilterConstants.loadErrorCodes.invalidPartFound;
              errorCode.details = invalidOpAmps.join(', ');
              loadDesignFailureAnalytics(errorCode.details, this.analytics);
            }
          }
        } else {
          errorCode.code = FilterConstants.loadErrorCodes.generalError;
        }
      } else {
        errorCode.code = FilterConstants.loadErrorCodes.noOpAmps;
      }
      //eslint-disable-next-line no-unused-vars
    } catch (err) {
      errorCode.code = FilterConstants.loadErrorCodes.generalError;
    } finally {
      if (errorCode.code === FilterConstants.loadErrorCodes.noError) {
        loadDesignAnalytics(parseData, this.analytics);
        returnData = { errorCode: errorCode, visitedTabs: inputs.visitedTabs, inputModel: inputs.inputModel };
      } else {
        returnData = { errorCode: errorCode, visitedTabs: null, inputModel: null };
      }
    }

    return returnData;
  }
  soak(data) {
    let output = [];
    let decode = decodeURIComponent(data);
    let p = parser([...decode]);
    let res = p.next();
    while (!res.done) {
      output.push(res.value);
      res = p.next();
    }
    return generateJsonString(processResults(output));
  }

  dry(tabs, inputModel, specificationResults, componentsAdvisor) {
    // Design Name
    let filterType = dasherize(inputModel.filterType);
    filterType = filterType.charAt(0).toUpperCase() + filterType.slice(1);
    let designName = filterType + ' Filter Design';
    // Design Description
    let designDesc = getOrdinal(specificationResults.nthOrder) + ' order ' + specificationResults.responseType;
    designDesc += specificationResults.filterResponseValue
      ? ' ' +
        specificationResults.filterResponseValue.toString() +
        (specificationResults.responseType === 'Chebyshev' ? ' dB' : '')
      : '';
    // Toolname and version
    let version = '1.3';
    let toolName = 'FW';
    // TimeStamp
    let now = new Date();
    let saveDateStamp =
      now.getFullYear().toString() +
      ('0' + (now.getMonth() + 1)).slice(-2) +
      ('0' + now.getDate()).slice(-2) +
      ('0' + now.getHours()).slice(-2) +
      ('0' + now.getMinutes()).slice(-2) +
      ('0' + now.getSeconds()).slice(-2);
    // Design Data
    let result = this.getSaveData(tabs, inputModel, componentsAdvisor);
    let savedDesign = result.design;
    // Compressed Design
    let dried =
      'N{name},D{desc},T{tool},V{version},C{cdate},U{udate},d[{tabs},{filter-type},{specifications},{components},{tolerances}]';
    dried = dried.replace('{name}', designName);
    dried = dried.replace('{desc}', designDesc);
    dried = dried.replace('{tool}', toolName);
    dried = dried.replace('{version}', version);
    dried = dried.replace('{cdate}', saveDateStamp);
    dried = dried.replace('{udate}', saveDateStamp);
    dried = dried.replace('{tabs}', dryVisitedTabs(savedDesign.visitedTabs));
    dried = dried.replace('{filter-type}', dryFilter(savedDesign.filterType));
    dried = dried.replace('{specifications}', drySpecifications(savedDesign.specifications));
    dried = dried.replace('{components}', dryComponents(savedDesign.components));
    dried = dried.replace('{tolerances}', dryTolerances(savedDesign.tolerances));
    // Circuit Components
    let components = buildCircuitComponents(
      result.model.results.nextSteps.detailsOfStages,
      result.model.results.nextSteps.detailsOfCircuits,
    );
    // Group Them all
    return {
      compact: dried,
      design: savedDesign,
      description: designDesc,
      components: components,
    };
  }

  hasOldAlgorithm(jsonStringData, inputModel) {
    const parseData = JSON.parse(jsonStringData);
    const version = parseData.Version;

    const filterType = version ? parseData.Design.filterType : (parseData.Design.type.filterType ?? '');
    // No filter type
    if (!filterType || filterType === '') return false;

    const specifications = parseData.Design.specifications;
    // No specifications
    if (!specifications || CommonUtils.isEmptyObject(specifications)) return false;

    const isWideBand =
      filterType === FilterConstants.filterType.bandPass &&
      2.5 * parseFloat(specifications.centerFrequency) < parseFloat(specifications.passbandFrequency);

    if (version) {
      // For designs with version numbers
      const components = parseData.Design.components;
      if (components && !CommonUtils.isEmptyObject(components)) {
        if (compareDesignVersion(version, '1.1') > 0) {
          // For Designs later than version 1.1
          const preferredAlgorithms = components.preferredAlgorithms;
          if (preferredAlgorithms) {
            const value = preferredAlgorithms['sallenKey|lowPass'];
            return value === 'lowpass';
          }
        } else {
          // For Designs version 1.1 and earlier
          if (filterType === FilterConstants.filterType.lowPass || isWideBand) {
            const stages = components.stages;
            const stageCount = isWideBand ? stages.length / 2 : stages.length;
            // In wideband case, the first half of the filter circuit is assumed to be low pass
            for (let i = 0; i < stageCount; i++) {
              if (stages[i].implementation === FilterConstants.implementationType.sallenKey) {
                return true;
              }
            }
          }
        }
      }
    } else {
      // Older designs that have no design versions do not have complete info on implementation,
      // especially during pick for me conditions. So we need to instantiate a model for this case.
      if (inputModel.components) {
        let indexModel = new IndexModel(this.opamps);
        indexModel.inputs.filterType = inputModel.filterType;
        indexModel.inputs.specifications = inputModel.specifications;
        indexModel.inputs.components = inputModel.components;
        const stages = indexModel.results.components.detailsOfStages;
        for (let i = 0; i < stages.length; i++) {
          const stage = stages[i];
          const stageImplementation = stage.implementation.replace(/ /g, '');
          const stageType = camelize(stage.filterName.substring(10).replace(/_/g, '-'));
          if (
            stageType === FilterConstants.filterType.lowPass &&
            stageImplementation === FilterConstants.implementationType.sallenKey
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }
}
